<script lang="ts">
const __DsBlock = {
	type: 'input',
	icon: 'lucide:text-cursor-input',
	name: '$t:blocks.input_textarea.name',
	description: '$t:blocks.input_textarea.description',
	category: 'input',
	tags: ['form-control'],
	fieldBinding: { supportedTypes: ['text'] },
	options: [
		{
			name: 'modelValue',
			label: '$t:blocks.common.props.value',
			model: true,
		},
		{
			name: 'placeholder',
			label: '$t:blocks.common.props.placeholder',
		},
		{
			name: 'autoresize',
			label: '$t:blocks.common.props.autoresize',
		},
		{
			name: 'variant',
			label: '$t:blocks.common.props.variant',
			type: 'enum',
			values: BLOCK_INPUT_VARIANT_OPTIONS,
		},
		{
			name: 'size',
			label: '$t:blocks.common.props.size',
			type: 'enum',
			values: BLOCK_CONTROL_SIZE_OPTIONS,
		},
		{
			name: 'leadingIcon',
			label: '$t:blocks.common.props.leading_icon',
			block: BuiltinBlockIds.InputIcon,
			nullable: true,
		},
		{
			name: 'trailingIcon',
			label: '$t:blocks.common.props.trailing_icon',
			block: BuiltinBlockIds.InputIcon,
			nullable: true,
		},
		{
			name: 'loading',
			label: '$t:blocks.common.props.loading',
		},
		{
			name: 'disabled',
			label: '$t:blocks.common.props.disabled',
		},
		{
			name: 'readonly',
			label: '$t:blocks.common.props.readonly',
		},
		{
			name: 'disabledNotice',
			label: '$t:blocks.common.props.disabled_notice',
		},
		{
			name: 'modelModifiers',
			hidden: true,
		},
	],
}
</script>
<script lang="ts" setup>
import type { InputBlockProps } from '#layers/blocks/types/input-block';
import type { TextareaProps } from '#layers/ui/public';
import { BuiltinBlockIds } from 'virtual:monospace-builtin-block-ids';
import { computed } from 'vue';

import { defineBlock } from '#layers/block-engine/public';
import { BLOCK_CONTROL_SIZE_OPTIONS, BLOCK_INPUT_VARIANT_OPTIONS } from '#layers/blocks/constants/ui-options';
import { usePrimitiveFormField } from '#layers/forms/public';
import { useDisabledNotice } from '#layers/ui/public';

interface Props extends InputBlockProps, Omit<TextareaProps, 'readonly' | 'color'> {
	autoresize?: boolean;
}

const {
	autoresize = true,
	rows: rowsProp,
	disabled: disabledProp,
	disabledNotice: disabledNoticeProp,
	placeholder,
	variant,
	size,
	leadingIcon,
	trailingIcon,
	loading,
	avatar,
	class: className,
	readonly,
} = defineProps<Props>();
defineSlots<{ trailing?: () => unknown }>();

const model = defineModel<string>();

;

const forwarded = computed(() => ({ placeholder, variant, size, leadingIcon, trailingIcon, loading, avatar, class: className, readonly }));
const { modelValue, disabled, disabledNotice } = usePrimitiveFormField(() => ({ disabled: disabledProp, disabledNotice: disabledNoticeProp }), { model });
const { showDisabledNotice } = useDisabledNotice({ disabled, disabledNotice });

const rows = computed(() => {
	if (rowsProp !== undefined) return rowsProp;
	return autoresize ? 5 : undefined;
});
</script>

<template>
	<DTooltip :delay-duration="0" :disabled="!showDisabledNotice" :text="disabledNotice">
		<DTextarea
			v-bind="forwarded"
			v-model="modelValue"
			:model-modifiers="modelModifiers ?? {}"
			:disabled
			class="min-w-full"
			:autoresize
			:rows
			:aria-readonly="readonly"
			:ui="{
				base: 'max-h-40 min-h-10 resize-y overflow-y-auto',
			}"
		>
			<template v-if="$slots.trailing" #trailing>
				<slot name="trailing" />
			</template>
		</DTextarea>
	</DTooltip>
</template>
